/**
 * View component for /evaluations/:evaluationId
 *
 * Displays a single evaluation from the 'byId' map in the evaluation reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import third-party libraries
import classNames from 'classnames';
import renderHTML from 'react-render-html';
import { DateTime } from 'luxon';

// import actions
import * as evaluationActions from '../evaluationActions';
import * as questionActions from '../../question/questionActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';

// import global components
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from "../../../global/components/routing/Breadcrumbs.js.jsx";
import Modal from '../../../global/components/modals/Modal.js.jsx';
import VideoPlayer from "../../../global/components/helpers/VideoPlayer.js.jsx";
import { NumberInput, SelectFromArray, SelectFromObject, TextInput } from '../../../global/components/forms';

// import module components
import EvaluationLayout from '../components/EvaluationLayout.js.jsx';
import QuestionEditor from '../../question/components/QuestionEditor.js.jsx';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';
import richContentUtils from '../../../global/utils/richContentUtils';
import videoUtils from "../../../global/utils/videoUtils.js";

// import evaluation css modules
import evaluationStyles from '../evaluationModuleStyles.css';

class AdminSingleEvaluation extends Base {
  constructor(props) {
    super(props);
    this.state = {
      editingEvaluation: null
      , newQuestion: _.cloneDeep(this.props.defaultQuestion)
      , showNewQuestion: false
      , showScoringModal: false
      , status: ''
    }
    this._bind(
      '_handleCreateQuestion'
      , '_handleDeleteQuestion'
      , '_handleFormStateChange'
      , '_handleStatusChange'
      , '_handleUpdateEval'
      , '_onVideoComplete'
      , '_openScoringModal'
      , '_saveStatusChange'

    )
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(evaluationActions.fetchSingleIfNeeded(match.params.evaluationId)).then((action) => {
      if(action.success) {
        dispatch(contentModuleActions.fetchSingleIfNeeded(action.item._contentModule));
      }
    });
    dispatch(questionActions.fetchListIfNeeded('_evaluation', match.params.evaluationId));
  }

  componentWillReceiveProps(nextProps) {
    // console.log("compone")
    const { dispatch, match, sortedQuestions } = nextProps;
    this.setState({sortedQuestions})
    dispatch(questionActions.fetchListIfNeeded('_evaluation', match.params.evaluationId));
  }

  _onVideoComplete(e) {
    console.log('video done');
    console.log(e);
  }

  _openScoringModal() {
    this.setState({
      editingEvaluation: _.cloneDeep(this.props.evaluationStore.selected.getItem())
      , showScoringModal: true
    })
  }

  _handleCreateQuestion() {
    const { defaultQuestion, dispatch, evaluationStore, match, questionStore } = this.props;
    const selectedEvaluation = evaluationStore.selected.getItem();
    let newQuestion = _.cloneDeep(this.state.newQuestion);
    newQuestion._evaluation = match.params.evaluationId;
    newQuestion._contentModule = selectedEvaluation._contentModule;

    dispatch(questionActions.sendCreateQuestion(newQuestion)).then((action) => {
      this.setState({
        newQuestion: _.cloneDeep(defaultQuestion)
        , showNewQuestion: false
      })
      dispatch(questionActions.invalidateList('_evaluation', match.params.evaluationId));
    })
  }

  _handleStatusChange(e) {
    let newState = _.update( this.state, e.target.name, function() {
      return e.target.value;
    });
  }

  _saveStatusChange() {
    console.log("_saveStatusChange")
    if(!this.state.status) {
      alert("You must select a status");
    } else {
      const { dispatch, evaluationStore } = this.props;
      let evaluation = _.cloneDeep(evaluationStore.selected.getItem());
      evaluation.status = this.state.status;
      dispatch(evaluationActions.sendUpdateEvaluation(evaluation)).then((action) => {
        if(action.success) {
          this.setState({
            changePublishStatus: false
            , status: ''
          });
        } else {
          // console.log("Response Error:");
          // console.log(action);
          alert(`ERROR: ${action.error}`);
        }
      });
    }
  }

  _handleUpdateEval() {
    const { dispatch } = this.props;

    dispatch(evaluationActions.sendUpdateEvaluation(this.state.editingEvaluation)).then((action) => {
      if(action.success) {
        this.setState({
          editingEvaluation: null
          , showScoringModal: false
        });
      } else {
        // console.log("Response Error:");
        // console.log(action);
        alert(`ERROR: ${action.error}`);
      }
    });
  }

  _handleDeleteQuestion(questionId) {
    const { dispatch, match } = this.props;
    // dispatch(answerActions.invalidateList('_quesiton', question._id));
    dispatch(questionActions.sendDelete(questionId)).then((action) => {
      dispatch(questionActions.invalidateList('_evaluation', match.params.evaluationId));
    })
  }

  _handleFormStateChange(e) {
    let newState = _.update( this.state, e.target.name, function() {
      return e.target.value;
    });
    this.setState(newState);
  }

  render() {
    const {
      defaultQuestion
      , evaluationStore
      , location
      , match
      , questionStore
      , questionList
      , sortedQuestions
      , contentModuleStore
    } = this.props;
    const { editingEvaluation, newQuestion, showNewQuestion, showScoringModal } = this.state;
    /**
     * use the selected.getItem() utility to pull the actual evaluation object from the map
     */
    const selectedEvaluation = evaluationStore.selected.getItem();
    const selectedContentModule = contentModuleStore.selected.getItem();
    // const questionList = questionStore.util.getList('_evaluation', match.params.evaluationId)
    const isEmpty = (
      !selectedEvaluation
      || !selectedEvaluation._id
      || evaluationStore.selected.didInvalidate
      || !questionList
      || !selectedContentModule
      || !selectedContentModule._id
    );

    let introHTML = null;
    if(selectedEvaluation && selectedEvaluation.introContent && selectedEvaluation.introContent.blocks && selectedEvaluation.introContent.blocks.length > 0) {
      introHTML = richContentUtils.editorState(selectedEvaluation.introContent);
    }

    let postEvalHTML = null;
    if(selectedEvaluation && selectedEvaluation.postEvalContent && selectedEvaluation.postEvalContent.blocks && selectedEvaluation.postEvalContent.blocks.length > 0) {
      postEvalHTML = richContentUtils.editorState(selectedEvaluation.postEvalContent);
    }

    let status = classNames(
      'status-pill'
      , { '-published': selectedEvaluation && selectedEvaluation.status === 'published'}
    )

    const introPlaylist = selectedEvaluation && videoUtils.returnPlaylist(selectedEvaluation.introVidPath, selectedEvaluation.introPosterPath, null); 
    const postPlaylist = selectedEvaluation && videoUtils.returnPlaylist(selectedEvaluation.postEvalVidPath, selectedEvaluation.postEvalPosterPath, null)

    return (
      <AuthoringLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <Breadcrumbs links={location.state.breadcrumbs} />
              {isEmpty ?
                (evaluationStore.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
                :
                <div style={{ opacity: evaluationStore.isFetching ? 0.5 : 1 }}>
                  <div className="yt-row with-gutters">
                    <div className="yt-col full s_60 m_70">
                      <div className="content-container">
                        <div className="yt-row space-between">
                          <p><strong>Evaluation intro content </strong></p>
                          <div>
                            <Link className="yt-btn x-small info" to={`${match.url}/update`}>Update content</Link>
                          </div>
                        </div>
                        <hr/>
                        <h2>
                          <small><strong>Title:</strong></small><br/>
                          {selectedEvaluation.title}
                        </h2>
                        <br/>
                        <small><strong>Introduction content:</strong></small>
                        {introPlaylist ? 
                          <VideoPlayer
                            isAutoPlay={false}
                            onVideoComplete={this._onVideoComplete}
                            playerId={selectedEvaluation._id + '_intro'}
                            playlist={introPlaylist}
                          />
                        : null
                        }
                        { introHTML ? renderHTML(introHTML) : ' n/a' }

                      </div>
                      <div className="content-container">
                        <p><strong>Questions & answers</strong></p>
                        <hr/>
                        <div className="questions-list">
                          { sortedQuestions.map((question, i) =>
                            !question ?
                              null
                            :
                              <QuestionEditor
                                handleDelete={this._handleDeleteQuestion}
                                key={question._id + i}
                                question={question}
                                questionNumber={i + 1}
                                trackingMetrics={selectedContentModule.metrics}
                              />
                          )}
                          { sortedQuestions.length < 1 ?
                            <h4 className="u-centerText"><em>No questions yet... </em></h4>
                            :
                            null
                          }
                        </div>
                        <div styleName="new-question">
                          { showNewQuestion ?
                            <div styleName="-form">
                              <div className="yt-row with-gutters">
                                <div className="yt-col full s_50 m_60">
                                  <TextInput
                                    change={this._handleFormStateChange}
                                    label="New question content"
                                    name="newQuestion.content"
                                    value={newQuestion.content}
                                    />
                                </div>
                                <div className="yt-col full s_30 m_20">
                                  <SelectFromObject
                                    change={this._handleFormStateChange}
                                    display={'name'}
                                    filterable={true}
                                    label="Answer type"
                                    name="newQuestion.answerType"
                                    objects={[
                                      {name:'Single correct answer (radios)', type: 'single'}
                                      , {name:'Multiple correct answers (checkboxes)', type:'multiple'}
                                    ]}
                                    selected={newQuestion.answerType}
                                    value={'type'}
                                    />
                                </div>
                                <div className="yt-col full s_20">
                                  <NumberInput
                                    change={this._handleFormStateChange}
                                    label="Question #"
                                    name="newQuestion.orderWeight"
                                    step="1"
                                    min="0"
                                    value={newQuestion.orderWeight}
                                  />
                                </div>
                              </div>
                              <div className="yt-row right">
                                <button className="yt-btn x-small link " onClick={() => this.setState({showNewQuestion: false, newQuestion: _.cloneDeep(defaultQuestion)})}>Cancel</button>
                                <button className="yt-btn x-small success" onClick={this._handleCreateQuestion}>Add question</button>
                              </div>
                            </div>
                            :
                            <div styleName="-prompt">
                              <button className="yt-btn success" onClick={() => this.setState({showNewQuestion: true})}><i className="fa fa-plus"/> New Question </button>
                            </div>
                          }
                        </div>
                      </div>
                      <div className="content-container">
                        <div className="yt-row space-between">
                          <p><strong>Post evaluation content </strong></p>
                          <div>
                            <Link className="yt-btn x-small info" to={`${match.url}/update`}>Update content</Link>
                          </div>
                        </div>
                        <hr/>

                        <p><strong>Post Evaluation Content</strong> </p>
                        {postPlaylist ? 
                          <VideoPlayer
                            isAutoPlay={false}
                            onVideoComplete={this._onVideoComplete}
                            playerId={selectedEvaluation._id + '_postEvaluation'}
                            playlist={postPlaylist}
                          />
                        : null
                        }
                        { postEvalHTML ? renderHTML(postEvalHTML) : 'n/a' }
                      </div>
                    </div>
                    <div className="yt-col full s_40 m_30">
                      <div className="content-container">
                        <p><strong>Status</strong></p>
                        <hr/>
                        <p>
                          Created on {DateTime.fromISO(selectedEvaluation.created).toLocaleString(DateTime.DATETIME_FULL)}
                          { selectedEvaluation._createdBy && userMap[selectedEvaluation._createdBy] ?
                            <span> by
                              <Link to={`/admin/users/${selectedEvaluation._createdBy}`} target="_blank"> {userMap[selectedEvaluation._createdBy].username}</Link>
                            </span>
                            : null
                          }
                        </p>
                        <p><small><em>Last updated {DateTime.fromISO(selectedEvaluation.updated).toLocaleString(DateTime.DATETIME_FULL)}</em></small></p>
                        <br/>
                        <p>This evaluation's publish status is:</p>
                        { this.state.changePublishStatus ?
                          <div>
                            <SelectFromArray
                              items={['draft','published']}
                              change={this._handleStatusChange}
                              name="status"
                              value={selectedEvaluation.status}
                            />
                            <div>
                              <button className="yt-btn x-small danger link" onClick={()=> this.setState({changePublishStatus: false})}>cancel</button>
                              <button className="yt-btn x-small info" onClick={()=> this._saveStatusChange()}>save</button>
                            </div>
                          </div>
                          :
                          <div>
                            <div className={status}>{_.startCase(selectedEvaluation.status).toUpperCase()}</div>
                            <button className="yt-btn x-small link danger" onClick={()=> this.setState({changePublishStatus: true})}>change</button>
                          </div>
                        }
                      </div>
                      <div className="content-container">
                        <p><strong>Scoring</strong></p>
                        <hr/>
                        <p><strong>Max 'FAILED': </strong> {selectedEvaluation.maxAllowedReds}</p>
                        <p><strong>Max 'CORRECT-ED': </strong> {selectedEvaluation.maxAllowedYellows}</p>
                        <p><strong>Min 'CORRECT': </strong> {selectedEvaluation.minAllowedGreens}</p>
                        <button className="yt-btn x-small link success" onClick={this._openScoringModal}>Update scoring</button>
                      </div>
                    </div>
                  </div>
                  <Modal
                    btnColor="info"
                    cardSize="standard"
                    closeAction={() => this.setState({showScoringModal: false})}
                    confirmAction={this._handleUpdateEval}
                    confirmText="Save & close"
                    isOpen={showScoringModal}
                    modalClasses="info"
                    modalHeader="Update scoring"
                  >
                    { !editingEvaluation ?
                      null
                      :
                      <div className="yt-col full m_50">
                        <NumberInput
                          change={this._handleFormStateChange}
                          label="MAXIMUM number of FAILED allowed"
                          name="editingEvaluation.maxAllowedReds"
                          min="0"
                          step="1"
                          value={editingEvaluation.maxAllowedReds}
                          />
                        <NumberInput
                          change={this._handleFormStateChange}
                          label="MAXIMUM number of CORRECT-ED allowed"
                          name="editingEvaluation.maxAllowedYellows"
                          min="0"
                          step="1"
                          value={editingEvaluation.maxAllowedYellows}
                          />
                        <NumberInput
                          change={this._handleFormStateChange}
                          label="MINIMUM number of CORRECT allowed"
                          name="editingEvaluation.minAllowedGreens"
                          min="0"
                          step="1"
                          value={editingEvaluation.minAllowedGreens}
                          />
                      </div>
                    }
                  </Modal>
                </div>
              }
            </div>
          </section>
        </div>
      </AuthoringLayout>
    )
  }
}

AdminSingleEvaluation.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store, ownProps) => {
  /**
   * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
   * differentiated from the React component's internal state
   */

  const questionList = store.question.util.getList('_evaluation', ownProps.match.params.evaluationId)
  const selectedEvaluation = store.evaluation.selected.getItem()
  let sortedQuestions = [];
  if(questionList && selectedEvaluation) {
    sortedQuestions = filterUtils.sortByOrderWeight(questionList);
    // sortedQuestions = !selectedEvaluation.randomizeQuestionOrder ? filterUtils.sortByOrderWeight(questionList) : filterUtils.randomizeOrder(questionList);
  }

  return {
    defaultQuestion: store.question.defaultItem
    , evaluationStore: store.evaluation
    , questionStore: store.question
    , contentModuleStore: store.contentModule
    , questionList
    , sortedQuestions
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminSingleEvaluation)
);



// WEBPACK FOOTER //
// modules/evaluation/views/AdminSingleEvaluation.js.jsx